import { useEffect, useMemo, useState } from 'react';

import toNonTechnicalType from './convertorToNonTechnicalType';
import { formatIPElements, formatNumberElements } from './radioElements';

import CheckboxInput from '@/Components/UI/Form/CheckboxInput/CheckboxInput';
import Label from '@/Components/UI/Form/Label/Label';
import { ROW_NUMBER_COLUMN_ID, ROW_NUMBER_COLUMN_NAME } from '@/Utils/gridConstants';
import '../style/index.scss';
import HelperText from '@/Components/UI/HelperText/HelperText';
import clsx from 'clsx';
import ComboBox from '@/Components/UI/Shadcn/ComboBox';

const initialTypeOptions = [
  { value: 'number', label: 'Number', type: 'Int64', format: 'decimal' },
  { value: 'plain_text', label: 'Plain Text', type: 'String', format: 'plain_text' },
  { value: 'ip_address', label: 'IP Address', type: 'IPv4', format: 'ipv4' },
  { value: 'date', label: 'Date', type: 'DateTime64', format: 'plain_text' },
  { value: 'plain_text', label: 'Email Address', type: 'EmailAddress', format: 'email_address' },
  { value: 'plain_text', label: 'Zip Code', type: 'ZipCode', format: 'zip_code' },
];

const ChangeDataType = ({
  options,
  targetColumn,
  confirmAction,
  setTargetColumn,
  setSaveBtnDisabled,
  updateChangeDataTypeState,
}) => {
  const [format, setFormat] = useState('');
  const [isConfirmDialogChecked, setIsConfirmDialogChecked] = useState(false);
  const [castType, setCastType] = useState('');
  const [srcType, setSrcType] = useState('');
  const [type, setType] = useState('');
  const [technicalSrcType, setTechnicalSrcType] = useState('');
  const [typeOptions, setTypeOptions] = useState(initialTypeOptions);
  const memoizedColumnsOptions = (options) => {
    return options.filter(
      ({ value, label }) => value !== ROW_NUMBER_COLUMN_ID || label !== ROW_NUMBER_COLUMN_NAME
    );
  };

  const excludeIdColumnOptions = useMemo(() => memoizedColumnsOptions(options), [options]);

  useEffect(() => {
    const finalTypeOptions =
      srcType === 'Boolean' || srcType === 'IP Address'
        ? initialTypeOptions.filter((option) => option.type !== 'DateTime64')
        : initialTypeOptions;
    setTypeOptions(finalTypeOptions);
  }, [srcType]);

  useEffect(() => {
    setSaveBtnDisabled(true);
    if (!targetColumn) return;

    onOriginalColumnChange(targetColumn);
  }, [targetColumn]);

  useEffect(() => {
    updateChangeDataTypeState({
      sourceColumn: targetColumn,
      castType: castType,
      format: format,
      isConfirmDialogChecked: isConfirmDialogChecked,
      srcType: srcType,
      technicalSrcType: technicalSrcType,
    });
  }, [targetColumn, castType, format, isConfirmDialogChecked, srcType, technicalSrcType]);

  const onOriginalColumnChange = (column) => {
    setSaveBtnDisabled(true);
    setTargetColumn(column);
    setSrcType(toNonTechnicalType(column.type));
    setTechnicalSrcType(column.type);
    setCastType('');
  };

  const onTypeChange = (column) => {
    setCastType(column[0].value);
    setFormat(column[0].format);
    setType(column[0].type);
    setSaveBtnDisabled(false);
  };

  return (
    <div data-cy='change-data-type-container'>
      <div>
        <div className='flex justify-between mb-2'>
          <Label text='Original Column' />
          {srcType ? (
            <HelperText text={`Type: ${srcType}`} color='capri' dataCy='custom-header-right' />
          ) : null}
        </div>
        <div className='mb-4'>
          <ComboBox
            value={targetColumn ? [targetColumn] : []}
            options={excludeIdColumnOptions}
            placeholder='Select Column'
            onChange={(_targetColumn) => setTargetColumn(_targetColumn[0])}
            dataCy='change-data-type-column'
          />
        </div>
        <Label text='New Column Type' />
        <div className='mb-4'>
          <ComboBox
            options={typeOptions}
            placeholder='Column Type'
            onChange={onTypeChange}
            value={typeOptions.filter((option) => option.type === type)}
            isDisabled={!targetColumn}
            dataCy='change-data-type-type'
          />
        </div>
        <div
          id='change-data-type-format-container py-2'
          hidden={['', 'plain_text', 'date'].includes(castType)}
          className='format-container'
        >
          <p className='h-5 text-sm font-semibold text-ui'>Format</p>
          <div hidden={castType !== 'ip_address'}>
            {formatIPElements(format).map((element, index) => {
              const { radioFormat, checked, text } = element;
              return (
                <div className='format-container-radio-block' key={index}>
                  <input
                    type='radio'
                    name='format_ip'
                    id={`format_ip-${index}`}
                    onChange={() => setFormat(radioFormat)}
                    checked={checked}
                  />
                  <label className='pl-2 text-sm' htmlFor={`format_ip-${index}`}>
                    {text}
                  </label>
                </div>
              );
            })}
          </div>
          <div hidden={castType !== 'number'}>
            {formatNumberElements(format).map((element, index) => {
              const { radioFormat, checked, text } = element;
              return (
                <div className='format-container-radio-block' key={index}>
                  <input
                    type='radio'
                    name='format_number'
                    id={`number_ip-${index}`}
                    onChange={() => setFormat(radioFormat)}
                    checked={checked}
                  />
                  <label className='pl-2 text-sm' htmlFor={`number_ip-${index}`}>
                    {text}
                  </label>
                </div>
              );
            })}
            <hr className={clsx('my-4 solid', !confirmAction && 'hidden')} />
          </div>
        </div>
      </div>
      {confirmAction ? (
        <div className='flex flex-col gap-2 pt-2'>
          <div data-cy='change-data-type-confirm-message' className='text-sm text-ui'>
            You may lose some data. The original column will be hidden. You can toggle its
            visibility in the columns panel.
          </div>
          <CheckboxInput
            onChange={() => setIsConfirmDialogChecked(!isConfirmDialogChecked)}
            label='Don’t show this message again'
            checked={isConfirmDialogChecked}
          />
        </div>
      ) : null}
    </div>
  );
};

export default ChangeDataType;
